import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useTheme } from "@/components/ThemeProvider";
import { Sun, Moon, ArrowRight, Sparkles, ShieldCheck, Star } from "lucide-react";
import SprintrollIcon from "@/components/SprintrollIcon";
import SprintrollLogo from "@/components/SprintrollLogo";
import ThreeViewsSection from "@/components/landing/ThreeViewsSection";
import FeaturesSection from "@/components/landing/FeaturesSection";
import WorkflowSection from "@/components/landing/WorkflowSection";
import UseCasesSection from "@/components/landing/UseCasesSection";
import PricingSection from "@/components/landing/PricingSection";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import TeamAdminSection from "@/components/landing/TeamAdminSection";
import NadIASection from "@/components/landing/NadIASection";
import FAQSection from "@/components/landing/FAQSection";
import EmailAuthForm from "@/components/auth/EmailAuthForm";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { user, loading } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative z-10 min-h-screen w-full">
      <SEO titleKey="seo.login.title" descriptionKey="seo.login.description" path="/login" />
      {/* ───── Top navigation ───── */}
      <header className="sticky top-0 z-30 glass-header" data-testid="landing-header">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <SprintrollLogo className="h-7 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-1 text-sm ml-4">
            <button onClick={() => goToSection("views")} data-testid="nav-views" className="px-3 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition">{t("landing.topnav.views")}</button>
            <button onClick={() => goToSection("features")} data-testid="nav-features" className="px-3 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition">{t("landing.topnav.features")}</button>
            <button onClick={() => goToSection("how")} data-testid="nav-how" className="px-3 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition">{t("landing.topnav.how")}</button>
            <button onClick={() => goToSection("pricing")} data-testid="nav-pricing" className="px-3 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition">{t("landing.topnav.pricing")}</button>
            <button onClick={() => goToSection("faq")} data-testid="nav-faq" className="px-3 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition">{t("landing.topnav.faq")}</button>
            <button onClick={() => goToSection("login")} data-testid="nav-login" className="px-3 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10 transition">{t("landing.topnav.signIn")}</button>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={toggle}
              data-testid="theme-toggle-landing"
              className="h-9 w-9 rounded-full grid place-items-center hover:bg-white/40 dark:hover:bg-white/10 transition"
              aria-label={t("nav.toggleTheme")}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={handleGoogle}
              data-testid="header-cta"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-sm font-medium transition shadow-md shadow-blue-500/30"
            >
              {t("landing.topnav.startFree")} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ───── HERO ───── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-soft opacity-50 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 pt-14 md:pt-24 pb-14 md:pb-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="space-y-7 animate-fade-rise">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 text-[11px] font-semibold uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5" /> {t("landing.hero.badge")}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-[1.02]">
              {t("landing.hero.title1")} <span className="text-blue-500">{t("landing.hero.title2")}</span> {t("landing.hero.title3")}
            </h1>
            <p
              className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t("landing.hero.subtitle") }}
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={handleGoogle}
                data-testid="hero-cta-google"
                className="inline-flex items-center justify-center gap-3 h-12 px-6 rounded-full bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium transition shadow-lg shadow-blue-500/30"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#fff" opacity="0.9" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#fff" opacity="0.9" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#fff" opacity="0.9" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                  <path fill="#fff" opacity="0.9" d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
                {t("landing.hero.startWithGoogle")}
              </button>
              <button
                onClick={() => goToSection("views")}
                data-testid="hero-cta-demo"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-blue-500/30 hover:bg-blue-500/5 font-medium transition"
              >
                {t("landing.hero.seeViews")}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> {t("landing.hero.encrypted")}
              </div>
              <div className="flex items-center gap-1.5">
                <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> {t("landing.hero.freeTrial")}
              </div>
              <div className="flex items-center gap-1.5" data-testid="hero-no-ai-training">
                <ShieldCheck className="h-4 w-4 text-blue-500" /> {t("landing.hero.noAiTraining")}
              </div>
            </div>
          </div>

          <div className="lg:pl-6 animate-float">
            <div className="relative max-w-md mx-auto">
              <div aria-hidden className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-blue-400/30 via-blue-500/15 to-indigo-500/20 blur-2xl" />
              <div className="relative glass-panel rounded-2xl p-6 shadow-[0_25px_60px_-15px_rgba(30,64,175,0.35)]" data-testid="hero-auth-card">
                <div className="text-center mb-4">
                  <h2 className="font-display text-xl font-semibold">{t("landing.authCard.title")}</h2>
                  <p className="text-xs text-muted-foreground mt-1">{t("landing.authCard.subtitle")}</p>
                </div>
                <button
                  onClick={handleGoogle}
                  data-testid="auth-card-google"
                  className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition shadow-md shadow-blue-500/25 mb-3"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                    <path fill="#fff" opacity="0.9" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#fff" opacity="0.9" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#fff" opacity="0.9" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                    <path fill="#fff" opacity="0.9" d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
                  </svg>
                  {t("landing.authCard.google")}
                </button>
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px flex-1 bg-white/40 dark:bg-white/10" />
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("landing.authCard.orEmail")}</span>
                  <div className="h-px flex-1 bg-white/40 dark:bg-white/10" />
                </div>
                <EmailAuthForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── 3 Vistas (Rolls · Sprints · Matriz) ───── */}
      <ThreeViewsSection />

      {/* ───── NadIA · AI project assistant ───── */}
      <NadIASection />

      {/* ───── Features ───── */}
      <FeaturesSection />

      {/* ───── How it works (animated) ───── */}
      <WorkflowSection />

      {/* ───── Use cases ───── */}
      <UseCasesSection />

      {/* ───── Team / Corporate features ───── */}
      <TeamAdminSection />

      {/* ───── Testimonials ───── */}
      <TestimonialsSection />

      {/* ───── Pricing ───── */}
      <PricingSection onCTAClick={handleGoogle} />

      {/* ───── FAQ + Privacy ───── */}
      <FAQSection onCTAClick={handleGoogle} />

      {/* ───── Login CTA (sign in card) ───── */}
      <section id="login" className="relative py-20 md:py-28" data-testid="landing-login">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass-panel rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center shadow-2xl shadow-blue-500/10">
            <div>
              <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
                {t("landingCTA.eyebrow")}
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-2 leading-[1.05]">
                {t("landingCTA.title")}
              </h2>
              <p className="text-muted-foreground mt-4 text-base md:text-lg">
                {t("landingCTA.subtitle")}
              </p>
              <ul className="space-y-2 mt-6 text-sm">
                {[
                  t("landingCTA.bullet1"),
                  t("landingCTA.bullet2"),
                  t("landingCTA.bullet3"),
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-muted-foreground">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 flex-none" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/70 dark:bg-slate-900/70 rounded-2xl p-7 border border-white/60 dark:border-white/10 shadow-lg">
              <h3 className="font-display text-2xl font-semibold">{t("landingCTA.welcome")}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t("landingCTA.welcomeSub")}
              </p>
              <button
                data-testid="login-google-button"
                onClick={handleGoogle}
                className="w-full mt-7 h-12 rounded-xl bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 shadow-md gap-3 inline-flex items-center justify-center font-medium transition"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
                {t("landingCTA.continueGoogle")}
              </button>
              <p className="text-xs text-muted-foreground mt-6 text-center leading-relaxed">
                {t("landingCTA.termsPre")}{" "}
                <Link to="/terms" data-testid="login-terms-link" className="text-blue-600 dark:text-blue-400 underline underline-offset-2">
                  {t("landingCTA.termsLink")}
                </Link>
                {t("landingCTA.termsPost")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── Footer ───── */}
      <footer className="border-t border-white/40 dark:border-white/10 py-10 mt-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <SprintrollLogo className="h-6 w-auto" />
            <span className="text-xs text-muted-foreground">© {new Date().getFullYear()} {t("footer.rights")}</span>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/terms"
              data-testid="footer-terms"
              className="text-xs text-muted-foreground hover:text-foreground transition"
            >
              {t("footer.terms")}
            </Link>
            <a
              href="https://mentedigital.com"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-mente-digital"
              className="text-xs text-muted-foreground inline-flex items-center gap-2 hover:opacity-90 transition group"
            >
              <span>{t("footer.createdBy")}</span>
              <span className="inline-flex items-center h-7 px-3 rounded-md bg-slate-900 dark:bg-white/5">
                <img
                  src="https://mentedigital.com/imagenes/logo_Mente_Digital_blanco.png"
                  alt="Mente Digital"
                  className="h-3.5 w-auto opacity-95"
                  loading="lazy"
                />
              </span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
